<template>
  <div class="inventory">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>一件代发</el-breadcrumb-item>
      <el-breadcrumb-item>我的库存</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="main">
      <div slot="header" class="card-head">
        <span>我的库存（只读；出库请在领星 OMS）</span>
        <span class="hint">可在【代发对接】维护领星 Key；同步由平台定时任务完成</span>
      </div>

      <div class="stat-row" v-loading="dashLoading">
        <div class="stat-card"><div class="stat-label">可用</div><div class="stat-value">{{ dash.availableQtySum || 0 }}</div></div>
        <div class="stat-card"><div class="stat-label">锁定</div><div class="stat-value">{{ dash.lockQtySum || 0 }}</div></div>
        <div class="stat-card"><div class="stat-label">在途</div><div class="stat-value">{{ dash.transportQtySum || 0 }}</div></div>
        <div class="stat-card warn"><div class="stat-label">出库异常</div><div class="stat-value">{{ dash.outboundException || 0 }}</div></div>
        <div class="stat-card"><div class="stat-label">已出库</div><div class="stat-value">{{ dash.outboundShipped || 0 }}</div></div>
        <div class="stat-meta">
          库存 {{ formatSyncTime(dash.inventorySyncTime) }}<br />
          出库 {{ formatSyncTime(dash.outboundSyncTime) }}
        </div>
      </div>

      <div class="toolbar">
        <el-select v-model="serviceCode" clearable placeholder="仓服务" style="width:140px" @change="handleSearch">
          <el-option label="美国代发" value="US" />
          <el-option label="日本代发" value="JP" />
        </el-select>
        <el-input
          v-if="needSkuFilter"
          placeholder="SKU"
          v-model="sku"
          clearable
          style="width:160px"
          @keyup.enter.native="handleSearch"
        />
        <el-input
          v-if="needKeyword"
          placeholder="单号/跟踪号"
          v-model="keyword"
          clearable
          style="width:180px"
          @keyup.enter.native="handleSearch"
        />
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </div>

      <el-tabs v-model="activeTab" @tab-click="onTabClick">
        <el-tab-pane label="库存" name="inventory">
          <el-table v-loading="loading" :data="inventoryRows" border stripe>
            <el-table-column prop="serviceName" label="服务" min-width="100" />
            <el-table-column prop="whCode" label="仓库" min-width="100" />
            <el-table-column prop="sku" label="SKU" min-width="120" />
            <el-table-column prop="productName" label="品名" min-width="160" show-overflow-tooltip />
            <el-table-column prop="availableQty" label="可用" width="90" align="right" />
            <el-table-column prop="lockQty" label="锁定" width="90" align="right" />
            <el-table-column prop="transportQty" label="在途" width="90" align="right" />
            <el-table-column label="品质" width="80">
              <template slot-scope="scope">
                {{ scope.row.stockType === '1' ? '次品' : '正品' }}
              </template>
            </el-table-column>
            <el-table-column label="同步时间" width="160">
              <template slot-scope="scope">{{ formatSyncTime(scope.row.syncTime) }}</template>
            </el-table-column>
            <template slot="empty">
              <span class="empty-tip">暂无库存。请先完成代发对接，并由平台同步领星库存</span>
            </template>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="出库" name="outbound">
          <el-table v-loading="loading" :data="outboundRows" border stripe>
            <el-table-column prop="outboundOrderNo" label="出库单号" min-width="130" />
            <el-table-column prop="status" label="状态" width="100">
              <template slot-scope="scope">
                <el-tag size="mini" :type="outboundStatusType(scope.row.status)">{{ outboundStatusText(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="whCode" label="仓" width="80" />
            <el-table-column prop="logisticsTrackNo" label="跟踪号" min-width="120" show-overflow-tooltip />
            <el-table-column prop="skuSummary" label="SKU" min-width="150" show-overflow-tooltip />
            <el-table-column prop="exceptionDesc" label="异常" min-width="120" show-overflow-tooltip />
            <el-table-column prop="orderCreateTime" label="创建" width="150" />
            <template slot="empty">
              <span class="empty-tip">暂无出库单缓存</span>
            </template>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="入库单" name="inbound">
          <el-table v-loading="loading" :data="inboundRows" border stripe>
            <el-table-column prop="inboundOrderNo" label="入库单号" min-width="130" />
            <el-table-column prop="status" label="状态" width="100">
              <template slot-scope="scope">{{ inboundStatusText(scope.row.status) }}</template>
            </el-table-column>
            <el-table-column prop="whCode" label="仓" width="80" />
            <el-table-column prop="referOrderNo" label="参考单号" min-width="120" show-overflow-tooltip />
            <el-table-column prop="localOrderNo" label="对照货代单" min-width="120" show-overflow-tooltip />
            <el-table-column prop="trackingNo" label="跟踪号" min-width="110" show-overflow-tooltip />
            <el-table-column prop="orderCreateTime" label="创建" width="150" />
            <template slot="empty">
              <span class="empty-tip">暂无入库单缓存</span>
            </template>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="库龄" name="stockAge">
          <el-table v-loading="loading" :data="stockAgeRows" border stripe>
            <el-table-column prop="sku" label="SKU" min-width="120" />
            <el-table-column prop="productName" label="品名" min-width="140" show-overflow-tooltip />
            <el-table-column prop="whCode" label="仓" width="90" />
            <el-table-column prop="totalAmount" label="数量" width="80" />
            <el-table-column prop="stockAge" label="库龄天" width="90" />
            <el-table-column prop="shelfDate" label="上架日" width="110" />
            <el-table-column prop="statisticDate" label="统计日" width="110" />
            <template slot="empty">
              <span class="empty-tip">暂无库龄数据（需已对接领星）</span>
            </template>
          </el-table>
        </el-tab-pane>
      </el-tabs>

      <div class="pagination-bar">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="page"
          :page-sizes="[20, 50, 100]"
          :page-size="rowsPerPage"
          layout="total, sizes, prev, pager, next"
          :total="total"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import api from "@/http/api";

export default {
  name: "Inventory",
  data() {
    return {
      loading: false,
      dashLoading: false,
      activeTab: "inventory",
      serviceCode: "",
      sku: "",
      keyword: "",
      inventoryRows: [],
      outboundRows: [],
      inboundRows: [],
      stockAgeRows: [],
      page: 1,
      rowsPerPage: 50,
      total: 0,
      dash: {}
    };
  },
  computed: {
    needSkuFilter() {
      return this.activeTab === "inventory" || this.activeTab === "stockAge";
    },
    needKeyword() {
      return this.activeTab === "outbound" || this.activeTab === "inbound";
    }
  },
  created() {
    this.loadDashboard();
    this.loadData();
  },
  methods: {
    formatSyncTime(ts) {
      if (ts == null || ts === "") return "—";
      try {
        const n = Number(ts);
        if (!Number.isNaN(n) && n > 0) {
          const ms = n < 1e12 ? n * 1000 : n;
          const d = new Date(ms);
          const pad = v => (v < 10 ? "0" + v : "" + v);
          return (
            d.getFullYear() +
            "-" +
            pad(d.getMonth() + 1) +
            "-" +
            pad(d.getDate()) +
            " " +
            pad(d.getHours()) +
            ":" +
            pad(d.getMinutes())
          );
        }
      } catch (e) {
        /* ignore */
      }
      return String(ts);
    },
    outboundStatusText(s) {
      return (
        { 0: "新建", 1: "已取面单", 2: "处理中", 3: "已出库", 4: "已取消", 5: "异常", 7: "取面单异常" }[s] ||
        String(s)
      );
    },
    outboundStatusType(s) {
      if (s === 3) return "success";
      if (s === 4) return "info";
      if (s === 5 || s === 7) return "danger";
      if (s === 1 || s === 2) return "warning";
      return "";
    },
    inboundStatusText(s) {
      return (
        { 0: "新建", 1: "待入库", 2: "收货中", 3: "已收货", 4: "已上架", 5: "已取消", 6: "待审核", 7: "驳回" }[s] ||
        String(s)
      );
    },
    handleSearch() {
      this.page = 1;
      this.loadData();
    },
    onTabClick() {
      this.page = 1;
      this.loadData();
    },
    handleSizeChange(val) {
      this.rowsPerPage = val;
      this.page = 1;
      this.loadData();
    },
    handleCurrentChange(val) {
      this.page = val;
      this.loadData();
    },
    applyPage(res, key) {
      if (res.data && res.data.code === "ok") {
        const data = res.data.data || {};
        this[key] = data.rows || data.list || [];
        this.total = data.total != null ? data.total : this[key].length;
      } else {
        this[key] = [];
        this.total = 0;
        this.$message.error((res.data && res.data.message) || "查询失败");
      }
    },
    loadDashboard() {
      this.dashLoading = true;
      api
        .queryInventoryDashboard()
        .then(res => {
          if (res.data && res.data.code === "ok") this.dash = res.data.data || {};
        })
        .catch(() => {})
        .finally(() => {
          this.dashLoading = false;
        });
    },
    loadData() {
      const map = {
        inventory: this.loadInventory,
        outbound: this.loadOutbound,
        inbound: this.loadInbound,
        stockAge: this.loadStockAge
      };
      (map[this.activeTab] || this.loadInventory)();
    },
    loadInventory() {
      this.loading = true;
      api
        .queryInventory({
          params: {
            serviceCode: this.serviceCode || undefined,
            sku: this.sku || undefined,
            page: this.page,
            rows: this.rowsPerPage
          }
        })
        .then(res => this.applyPage(res, "inventoryRows"))
        .catch(() => {
          this.inventoryRows = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadOutbound() {
      this.loading = true;
      api
        .queryOutbound({
          params: {
            serviceCode: this.serviceCode || undefined,
            keyword: this.keyword || undefined,
            page: this.page,
            rows: this.rowsPerPage
          }
        })
        .then(res => this.applyPage(res, "outboundRows"))
        .catch(() => {
          this.outboundRows = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadInbound() {
      this.loading = true;
      api
        .queryInbound({
          params: {
            serviceCode: this.serviceCode || undefined,
            keyword: this.keyword || undefined,
            page: this.page,
            rows: this.rowsPerPage
          }
        })
        .then(res => this.applyPage(res, "inboundRows"))
        .catch(() => {
          this.inboundRows = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadStockAge() {
      this.loading = true;
      api
        .queryStockAge({
          params: {
            sku: this.sku || undefined,
            page: this.page,
            rows: this.rowsPerPage
          }
        })
        .then(res => this.applyPage(res, "stockAgeRows"))
        .catch(() => {
          this.stockAgeRows = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.main {
  margin-top: 12px;
}
.card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
}
.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.hint {
  color: #909399;
  font-size: 12px;
  font-weight: normal;
}
.pagination-bar {
  margin-top: 12px;
  text-align: right;
}
.empty-tip {
  color: #909399;
}
.stat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
  align-items: stretch;
}
.stat-card {
  min-width: 88px;
  padding: 10px 14px;
  background: #f5f7fa;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}
.stat-card.warn {
  background: #fef0f0;
  border-color: #fde2e2;
}
.stat-label {
  font-size: 12px;
  color: #909399;
}
.stat-value {
  margin-top: 4px;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
.stat-meta {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  padding: 8px 4px;
}
</style>
